import React, { useEffect, useMemo, useState, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  GetAllRetaionData,
  HandleFamily,
  isProviderCodeExist,
  HandlePostUserRelation,
  HandlePostBasic,
} from "../utils/Api";
import {
  setBasicData,
  setConsultData,
  setLoading,
  setTempData,
  setUserData,
} from "../Redux/Actions/Actions";
import Popup from "../components/Popup";
import { toast } from "react-toastify";
import DynamicModal from "../components/DynamicModal";

export default function NameDetailsPage() {
  const UserData = useSelector((state) => state.User);
  const Basic = useSelector((state) => state.Basic);
  const MainUser = useSelector((state) => state.MainUser);

  const [firstName, setFirstName] = useState("");
  const [LastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [providerCode, setProviderCode] = useState(null);

  const [relations, setRelations] = useState([]);
  const [relation, setRelation] = useState("");
  const [isRelation, setIsRelation] = useState(false);
  const [open, setOpen] = useState(false);
  const [profiles, setProfiles] = useState([]);
  const [modal, setModal] = useState({
    show: false,
    type: "warning",
    title: "",
    msg: "",
    onConfirm: null,
  });

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const initialized = useRef(false);

  // Sync provider code from Redux Basic data
  useEffect(() => {
    if (Basic.providerCode) {
      setProviderCode(Basic.providerCode);
    }
  }, [Basic.providerCode]);

  // Memoized check for provider code visibility
  const isShowingProvidedCode = useMemo(() => {
    if (Basic.profileCode === "careally" && Basic.providerCode) {
      return false;
    }
    return true;
  }, [Basic.providerCode, Basic.profileCode]);

  // Sync state with Redux UserData
  useEffect(() => {
    if (UserData.firstName) {
      setFirstName(UserData.firstName);
      setLastName(UserData.LastName);
      setEmail(UserData.email);
      if (UserData.Relation && UserData.Relation !== "self") {
        setIsRelation(true);
        setRelation(UserData.Relation);
      }
    }
  }, [UserData]);

  // Initial Data Fetching (Prevents double hit)
  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;

    dispatch(setLoading({ loading: false, otp: false }));

    // Fetch Family Profiles if Gender exists
    if (Basic.Gender != null) {
      const initialProfile = {
        userID: MainUser.userID,
        type: MainUser.type,
        firstName: MainUser.firstName,
        Gender: MainUser.Gender,
        authToken: MainUser.authToken,
        age: "some",
      };
      setProfiles([initialProfile]);

      HandleFamily(MainUser.userID, MainUser.type).then(async (res) => {
        if (res.ok) {
          const data = await res.json();
          if (data.relations) {
            setProfiles((prev) => [...prev, ...data.relations]);
          }
        }
      });

      if (Basic.rid != null) setOpen(true);
    }

    // Fetch Relation Dropdown Data
    GetAllRetaionData().then(async (res) => {
      const d = await res.json();
      if (d.status === true) setRelations(d.data);
    });
  }, [Basic.Gender, Basic.rid, MainUser, dispatch]);

  const proceedWithRegistration = async (headers, payload) => {
    try {
      const apiCall =
        Basic.rid == null
          ? HandlePostUserRelation(headers, payload)
          : HandlePostBasic(headers, payload);

      const response = await apiCall;
      const result = await response.json();

      if (result.status === true) {
        // Update basic data immutably
        const updatedBasic = {
          ...Basic,
          id: result.userID || Basic.id,
          type: result.type || Basic.type,
          authToken: result.authToken || Basic.authToken,
          rid: result.userID || Basic.rid,
        };

        dispatch(setBasicData(updatedBasic));
        dispatch(setUserData({ ...UserData, ...payload }));
        navigate("/other-details");
      } else {
        toast.error(result.message);
      }
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate email format if provided
    if (email && email.trim() !== "") {
      const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      if (!emailRegex.test(email.trim())) {
        return toast.error("Please enter a valid email address!");
      }
    }

    if (Basic.profileCode === "careally" && !providerCode) {
      window.ReactNativeWebView?.postMessage(
        JSON.stringify({
          type: "OPEN_BOTTOM_SHEET",
          data: { screen: "PROVIDER_CODE" },
        }),
      );
      return toast.error("Please enter provider code to continue!");
    }

    const headers = {
      "Content-Type": "application/json",
      device: "Android",
      Accept: "application/json",
      CorrelationId: Date.now(), // More reliable than hardcoded string
      AuthToken: Basic.authToken,
      userId: Basic.id,
    };

    const payload = {
      firstName,
      LastName,
      email,
      Relation: "self",
      ConnectionID: MainUser.userID,
      mobileNumber: MainUser.mobile,
      mobileCode: MainUser.mobileCode,
      providerCode: providerCode || "",
    };

    try {
      if (providerCode.length > 0) {
        const validation = await isProviderCodeExist(providerCode, headers);
        if (!validation.isExist) {
          setModal({
            show: true,
            type: "error",
            title: "Registration Alert",
            msg: `Wrong provider code. Please input valid provider code.`,
          });
        } else {
          setModal({
            show: true,
            type: "warning",
            title: "Registration Alert",
            msg: `You are about to register with ${validation.doctorName} as your provider. Would you like to continue?`,
            onConfirm: () => proceedWithRegistration(headers, payload),
          });
        }
      } else {
        setModal({
          show: true,
          type: "warning",
          title: "Registration Alert",
          msg: `You are about to register with myGleeo as your provider. Would you like to continue?`,
          onConfirm: () => proceedWithRegistration(headers, payload),
        });
      }
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    }
  };

  const onClose = (data) => {
    setOpen(false);

    // Reset or Switch User Logic
    if (data == null) {
      dispatch(setBasicData({ ...Basic, rid: null }));
      setIsRelation(true);
      setEmail("");
      setFirstName("");
      setLastName("");
      setRelation("");
    } else {
      const isExisted = data.Gender != null;
      const updatedBasic = {
        ...Basic,
        isExisted,
        id: data.userID,
        name: data.firstName,
        type: data.type,
        authToken: data.authToken,
        rid: isExisted ? null : data.userID,
        qs: "",
      };

      dispatch(setBasicData(updatedBasic));
      dispatch(setTempData({ null: "null" }));
      dispatch(setConsultData({ null: "null" }));

      if (!isExisted) {
        dispatch(setUserData(data));
      } else {
        dispatch(setUserData({ null: "null" }));
        window.ReactNativeWebView?.postMessage(
          JSON.stringify({ type: "LOGIN_SUCESS", data: updatedBasic }),
        );
      }
    }
  };

  return (
    <div className="flex justify-center items-center text-thtext bg-gray-100 overflow-y-auto">
      <Popup isOpen={open} onClose={onClose} data={profiles} />
      <DynamicModal
        isOpen={modal.show}
        onClose={() =>
          setModal((prev) => ({ ...prev, show: false, onConfirm: null }))
        }
        type={modal.type}
        title={modal.title}
        message={modal.msg}
        onConfirm={modal.onConfirm}
      />
      <div className="min-h-screen container items-center ">
        <div className="h-screen flex flex-col justify-start items-center ">
          <div className=" max-w-md w-full h-44 bg-detailsbg">
            <img
              className="w-full h-full pt-5"
              src="./assets/Details.svg"
              alt="Details Header"
            />
          </div>
          <div className="px-8 pt-6 pb-8 max-w-md w-full h-full font-bold">
            <h2 className="text-lg text-left mb-6">Enter patient's details</h2>
            <form
              className="flex flex-col justify-between h-3/4"
              onSubmit={handleSubmit}
            >
              <div className="grid grid-cols-2 gap-4">
                <div className="py-2">
                  <h4 className="text-sm">First Name</h4>
                  <input
                    className="pt-2 focus:outline-none w-full bg-transparent border-b border-gray-300"
                    type="text"
                    required
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                  />
                </div>
                <div className="py-2">
                  <h4 className="text-sm">Last Name</h4>
                  <input
                    className="pt-2 focus:outline-none w-full bg-transparent border-b border-gray-300"
                    type="text"
                    required
                    value={LastName}
                    onChange={(e) => setLastName(e.target.value)}
                  />
                </div>
                <div className="py-2 col-span-2">
                  <h4 className="text-sm">Email</h4>
                  <input
                    className="pt-2 focus:outline-none w-full bg-transparent border-b border-gray-300"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
                {isShowingProvidedCode && (
                  <div className="py-2 col-span-2">
                    <h4 className="text-sm">
                      Provider Code (given by your doctor/hospital)
                    </h4>
                    <input
                      className="pt-2 focus:outline-none w-full bg-transparent border-b border-gray-300"
                      type="text"
                      value={providerCode || ""}
                      onChange={(e) => setProviderCode(e.target.value)}
                    />
                  </div>
                )}
              </div>
              <button
                className="bg-primary h-12 text-white font-bold py-2 px-4 mt-6 rounded-full w-full"
                type="submit"
              >
                Next
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
